import React from "react";
import styles from "./ReferModal.module.css";
import Hero from "Assets/About/about_1.png";
import { useForm } from "react-hook-form";
import { Referral_Create } from "Services/Requests";
import { Controller } from "react-hook-form";
import {
    showErrorNotification,
    showSuccessNotification,
} from "Utils/Notifications";
import { useStore } from "Store";

type FormValues = {
    friend_name: string;
    friend_email: string;
    friend_phone_number: string;
    state?: string;
};

const ReferModal: React.FC = () => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
        control,
    } = useForm<FormValues>({ mode: "onBlur" });
    const [submitting, setSubmitting] = React.useState(false);
    const setShow = useStore((s: any) => s.setShowReferModal);
    const show = useStore((s: any) => s.showReferModal);
    let formData = watch();
    if (!show) return null;

    const onSubmit = async (data: FormValues) => {
        setSubmitting(true);
        try {
            const res = await Referral_Create(data);
            // Axios response: data may be in res.data
            if (res && res.status === 200) {
                const message =
                    (res.data && res.data.Message) || "Referral sent";
                showSuccessNotification(message);
                reset();
                setShow(false);
            } else {
                const message =
                    (res?.data && res.data.Message) ||
                    "Failed to send referral";
                showErrorNotification(message);
            }
        } catch (err: any) {
            const message =
                err?.response?.data?.Message ||
                err?.message ||
                "Something went wrong";
            showErrorNotification(message);
        } finally {
            setSubmitting(false);
        }
    };
    const formatMobile = (value: any) => {
        if (value) {
            const digits = value.replace(/\D/g, "").slice(0, 10);
            let formatted = "";

            if (digits.length > 0) formatted += digits.slice(0, 3); // First 3 digits
            if (digits.length > 3) formatted += "-" + digits.slice(3, 6); // Next 3 digits
            if (digits.length > 6) formatted += "-" + digits.slice(6, 10); // Last 4 digits

            return formatted;
        }
    };

    return (
        <div className={styles.overlay} onClick={() => setShow(false)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalInner}>
                    <div className={styles.leftPanel}>
                        <img src={Hero} alt="refer" />
                        <div className={styles.leftOverlay}>
                            <h4>Premier Referral</h4>
                            <p>
                                Refer a friend and earn rewards. Fast support
                                and priority handling for referred clients.
                            </p>
                        </div>
                    </div>
                    <div className={styles.rightPanel}>
                        <h3>Refer a Friend</h3>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className={styles.formRow}>
                                <label>Referral Name</label>
                                <input
                                    {...register("friend_name", {
                                        required: "Name is required",
                                    })}
                                />
                                {errors.friend_name && (
                                    <span className={styles.error}>
                                        {errors.friend_name.message}
                                    </span>
                                )}
                            </div>

                            <div className={styles.formRow}>
                                <label>Referral Email</label>
                                <input
                                    {...register("friend_email", {
                                        required: false,
                                        pattern: {
                                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                            message: "Enter valid email",
                                        },
                                    })}
                                />
                                {errors.friend_email && (
                                    <span className={styles.error}>
                                        {errors.friend_email.message}
                                    </span>
                                )}
                            </div>

                            <div className={styles.formRow}>
                                <label>Friend Phone Number</label>

                                <Controller
                                    name="friend_phone_number"
                                    control={control}
                                    defaultValue=""
                                    rules={{
                                        pattern: {
                                            value: /^\d{3}-\d{3}-\d{4}$/,
                                            message:
                                                "Invalid format. Use XXX-XXX-XXXX for mobile number",
                                        },
                                    }}
                                    render={({
                                        field: { onChange, onBlur, value, ref },
                                    }) => (
                                        <input
                                            ref={ref}
                                            value={formatMobile(value)}
                                            onChange={(e) => {
                                                const formattedValue: any =
                                                    formatMobile(
                                                        e.target.value
                                                    );
                                                const cursorPosition =
                                                    e.target.selectionStart ??
                                                    0;
                                                onChange(formattedValue);
                                                setTimeout(() => {
                                                    const inputElement =
                                                        e.target;
                                                    // Adjust the cursor position after formatting
                                                    let newCursorPosition =
                                                        cursorPosition;

                                                    // Handle cursor positioning when hyphens are added or removed
                                                    // The cursor moves to the right after each character typed, accounting for hyphens

                                                    if (
                                                        formattedValue &&
                                                        formattedValue[
                                                            cursorPosition - 1
                                                        ] === "-" &&
                                                        !formattedValue[
                                                            cursorPosition + 1
                                                        ] &&
                                                        formattedValue.length >
                                                            formData
                                                                .friend_phone_number
                                                                ?.length &&
                                                        cursorPosition > 1
                                                    ) {
                                                        newCursorPosition++; // If cursor was on a hyphen, move it right
                                                    }
                                                    inputElement.setSelectionRange(
                                                        newCursorPosition,
                                                        newCursorPosition
                                                    ); // Set cursor next to typed character
                                                }, 0);
                                            }}
                                            onBlur={onBlur}
                                            placeholder="Mobile Number "
                                        />
                                    )}
                                />
                                {errors.friend_phone_number && (
                                    <span className={styles.error}>
                                        {errors.friend_phone_number.message}
                                    </span>
                                )}
                            </div>

                            <div className={styles.formRow}>
                                <label>
                                    State{" "}
                                    <span className={styles.optional}>
                                        (optional)
                                    </span>
                                </label>
                                <input {...register("state")} />
                            </div>

                            <div className={styles.formActions}>
                                <button
                                    type="button"
                                    className={styles.btnSecondary}
                                    onClick={() => setShow(false)}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className={styles.btnPrimary}
                                    disabled={submitting}>
                                    {submitting
                                        ? "Sending..."
                                        : "Send Referral"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReferModal;
